/**
 * TimelineEvent Component
 *
 * Individual event card for the project timeline.
 * Displays event details with icon, timestamp, and user info.
 * Supports expandable details for task activity events.
 *
 * @module components/timeline/TimelineEvent
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Badge, Collapse } from 'react-bootstrap';
import {
  EVENT_TYPE_LABELS,
  EVENT_TYPE_ICONS,
  EVENT_TYPE_COLORS,
} from '../../services/timelineService';
import { formatCurrency } from '../../utils/formatters';
import TimelineEventDetails from './TimelineEventDetails';

/**
 * Format time for display
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted time
 */
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * TimelineEvent component
 * @param {Object} props
 * @param {Object} props.event - Event data
 * @param {boolean} props.showDate - Whether to show date (for non-grouped display)
 */
const TimelineEvent = ({ event, showDate = false, basePath = '/mensajes', isHighlighted = false }) => {
  const navigate = useNavigate();
  const {
    event_type,
    description,
    user_name,
    timestamp,
    is_milestone,
    photo_url,
    amount,
    metadata,
    source,
  } = event;

  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted]);

  const icon = EVENT_TYPE_ICONS[event_type] || 'bi-circle';
  const color = EVENT_TYPE_COLORS[event_type] || 'secondary';
  const label = EVENT_TYPE_LABELS[event_type] || event_type;

  // Check if this event is expandable
  const isExpandable = metadata?.expandable === true;

  // Handle click to toggle expansion
  const handleClick = () => {
    if (isExpandable) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      ref={cardRef}
      className={`timeline-event ${is_milestone ? 'timeline-event-milestone' : ''} ${isHighlighted ? 'timeline-event-highlighted' : ''}`}
    >
      <div className="timeline-event-marker">
        <div className={`timeline-event-icon bg-${color}`}>
          <i className={`bi ${icon}`}></i>
        </div>
        <div className="timeline-event-line"></div>
      </div>

      <Card
        className={`timeline-event-card ${is_milestone ? 'border-warning' : ''} ${isExpandable ? 'expandable' : ''} ${isExpanded ? 'expanded' : ''}`}
        onClick={handleClick}
        style={{ cursor: isExpandable ? 'pointer' : 'default' }}
      >
        <Card.Body className="py-2 px-3">
          {/* Header: Time and milestone badge */}
          <div className="d-flex justify-content-between align-items-start mb-1">
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">
                {formatTime(timestamp)}
                {showDate && (
                  <span className="ms-2">
                    {new Date(timestamp).toLocaleDateString('es-CR')}
                  </span>
                )}
              </small>
              {is_milestone && (
                <Badge bg="warning" text="dark" className="badge-sm">
                  <i className="bi bi-flag-fill me-1"></i>
                  Hito
                </Badge>
              )}
              {/* Task badge for task activity */}
              {metadata?.task_number && (
                <Badge bg="secondary" className="badge-sm">
                  {metadata.task_number}
                </Badge>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={`${color}`} className="badge-sm text-capitalize">
                {label}
              </Badge>
              {/* Expand indicator */}
              {isExpandable && (
                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`}></i>
              )}
            </div>
          </div>

          {/* Description */}
          <p className="mb-1 timeline-event-description">{description}</p>

          {/* Attachment indicator for comments */}
          {metadata?.has_attachments && (
            <div className="timeline-event-attachment-indicator mb-1">
              <Badge bg="light" text="dark" className="badge-sm">
                <i className="bi bi-image me-1"></i>
                Con imagenes
              </Badge>
            </div>
          )}

          {/* Photo thumbnail (for photo events - not task activity) */}
          {photo_url && source === 'progress_photo' && (
            <div className="timeline-event-photo mt-2">
              <a href={photo_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                <img
                  src={photo_url}
                  alt="Foto de progreso"
                  className="img-thumbnail"
                  style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'cover' }}
                />
              </a>
            </div>
          )}

          {/* Amount (for expense events) */}
          {amount && source === 'expense' && (
            <div className="timeline-event-amount mt-1">
              <Badge bg="danger" className="badge-sm">
                <i className="bi bi-currency-dollar me-1"></i>
                {formatCurrency(amount)}
              </Badge>
            </div>
          )}

          {/* Quotation amount */}
          {amount && source === 'quotation' && (
            <div className="timeline-event-amount mt-1">
              <Badge bg="success" className="badge-sm">
                <i className="bi bi-currency-dollar me-1"></i>
                {formatCurrency(amount)}
              </Badge>
            </div>
          )}

          {/* Receipt link (for expenses with receipts) */}
          {metadata?.receipt_url && !isExpandable && (
            <a
              href={metadata.receipt_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-secondary mt-2"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="bi bi-file-earmark-pdf me-1"></i>
              Ver Recibo
            </a>
          )}

          {/* Chat linked: link to conversation */}
          {event_type === 'chat_linked' && metadata?.conversation_id && (
            <button
              className="btn btn-sm btn-outline-primary mt-2"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${basePath}/${metadata.conversation_id}`);
              }}
            >
              <i className="bi bi-chat-left-quote me-1"></i>
              Ver Conversación
            </button>
          )}

          {/* User info */}
          <div className="timeline-event-user mt-2">
            <small className="text-muted">
              <i className="bi bi-person me-1"></i>
              {user_name || 'Sistema'}
            </small>
          </div>

          {/* Expandable details section */}
          <Collapse in={isExpanded}>
            <div>
              {isExpanded && (
                <TimelineEventDetails event={event} />
              )}
            </div>
          </Collapse>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TimelineEvent;
